import { useMemo, useState } from "react";
import { researchArtifactHref, researchPageHref } from "../../../shared/wire/research.ts";
import type { CheckedRef, ResearchCapabilities, ResearchMapGap, ResearchMapNode, ResearchMapView, ResearchNotice, ResearchScopeRef } from "../types";
import { canvasMeasurer, useElementWidth } from "./measure";
import { openInApp } from "./Research";
import { layoutResearchMap, measureLabel, neighboursOf, type PlacedResearchEdge, type ResearchLayout } from "./researchGraphLayout";
import { authorityLabel, CLASSIFICATION_LABEL, kindLabel, noticeLabel, recordStatusLabel, sourceStatusLabel, timeLabel } from "./researchLabels";
import { orderGaps, snapshotLabel, tombstone, unavailableDomains } from "./researchMapView";

const FONT = "13px system-ui, sans-serif";

/** The footer a map carries: its time, its version and the domains it cannot see. */
export function ResearchMapMeta({ view, refreshedAt }: { view: { snapshotAt: number; revision: string; capabilities: ResearchCapabilities }; refreshedAt: number | null }) {
	const unavailable = unavailableDomains(view.capabilities);
	return (
		<p className="research-map-meta">
			<span>{snapshotLabel(view.snapshotAt, refreshedAt)}</span>
			<span>版本 {view.revision}</span>
			{unavailable.length > 0 && <span>未部署：{unavailable.join("、")}</span>}
		</p>
	);
}

/** What the map says needs a look: sources that are not current, domains that are unavailable, bindings nobody confirmed. */
export function MapNotices({ notices }: { notices: ResearchNotice[] }) {
	if (notices.length === 0) return null;
	return (
		<ul className="research-notices">
			{notices.map((n, i) => (
				<li key={`${n.kind}:${n.path ?? n.recordId ?? n.domain ?? i}`} className={n.kind === "source_problem" && (n.sourceStatus === "missing" || n.sourceStatus === "moved") ? "error" : n.kind === "source_problem" || n.kind === "not_associated" ? "warn" : ""}>
					{noticeLabel(n)}
					{n.message ? ` · ${n.message}` : ""}
				</li>
			))}
		</ul>
	);
}

/** A record's sources, each with what it looks like now and the snapshot kept of it. */
export function Sources({ refs, scopeRef }: { refs: CheckedRef[]; scopeRef: ResearchScopeRef }) {
	if (refs.length === 0) return <span className="hint">暂无材料</span>;
	return (
		<ul className="research-sources">
			{refs.map((source, i) => (
				<li key={`${source.path ?? source.url ?? source.citekey ?? source.handle ?? i}`}>
					<SourceLine source={source} scopeRef={scopeRef} />
				</li>
			))}
		</ul>
	);
}

function SourceLine({ source, scopeRef }: { source: CheckedRef; scopeRef: ResearchScopeRef }) {
	const stone = tombstone(source);
	const name = source.path ?? source.url ?? source.citekey ?? source.handle ?? "材料";
	const snapshot = typeof source.artifactId === "string" ? researchArtifactHref(scopeRef, source.artifactId) : null;
	return (
		<>
			{stone ? (
				<span className={`research-tag ${source.sourceStatus} research-tombstone`}>{stone.label}</span>
			) : (
				source.sourceStatus && <span className={`research-tag ${source.sourceStatus}`}>{sourceStatusLabel(source.sourceStatus)}</span>
			)}
			{source.url ? (
				<a href={source.url} target="_blank" rel="noreferrer">
					{name}
				</a>
			) : (
				<code>{name}</code>
			)}
			{stone?.movedTo && <span className="hint"> → {stone.movedTo}</span>}
			{source.lastCheckedAt !== undefined && <span className="hint"> · 检查于 {timeLabel(source.lastCheckedAt)}</span>}
			{snapshot && (
				<>
					{" "}
					<a href={snapshot} target="_blank" rel="noreferrer">
						{stone ? "旧快照" : "快照"}
						{source.capturedAt !== undefined ? ` ${timeLabel(source.capturedAt)}` : ""}
					</a>
				</>
			)}
		</>
	);
}

/** What is picked on the map: a node, a gap, or a record that is no node (a binding, a relation, a rejected one). */
export type ResearchSelection = { kind: "node"; id: string } | { kind: "gap"; id: string } | { kind: "record"; id: string } | null;

/** The selection a record id makes on a map: its node, the gap it backs, else the bare record. */
export function selectionFor(map: ResearchMapView, id: string): ResearchSelection {
	if (map.nodes.some((n) => n.id === id)) return { kind: "node", id };
	const gap = map.gaps.find((g) => g.target?.id === id);
	return gap ? { kind: "gap", id: gap.id } : { kind: "record", id };
}

/**
 * The map: the graph in group bands (a list when crowded or narrow), the gaps in their order, the notices, the
 * association queue, and the detail of what is selected. Shared by the chat renderer and the research page.
 */
export function ResearchMapPanel({
	map,
	scopeRef,
	selected: controlled,
	onSelect,
	onFocus,
	renderRecord,
}: {
	map: ResearchMapView;
	scopeRef: ResearchScopeRef;
	selected?: ResearchSelection;
	onSelect?: (selection: ResearchSelection) => void;
	/** Offered on a node when the view can expand around it. */
	onFocus?: (id: string) => void;
	/** Drawn under the detail of the selected record (a node, a gap's record or a bare record): its history and the owner's actions. */
	renderRecord?: (recordId: string) => React.ReactNode;
}) {
	const [own, setOwn] = useState<ResearchSelection>(null);
	const selected = controlled !== undefined ? controlled : own;
	const select = onSelect ?? setOwn;
	const [box, width] = useElementWidth(480);
	const measure = useMemo(() => canvasMeasurer(FONT, measureLabel), []);
	const layout = useMemo(() => layoutResearchMap(map, width, measure), [map, width, measure]);
	const gaps = useMemo(() => orderGaps(map.gaps), [map.gaps]);
	const node = selected?.kind === "node" ? (map.nodes.find((n) => n.id === selected.id) ?? null) : null;
	const gap = selected?.kind === "gap" ? (gaps.find((g) => g.id === selected.id) ?? null) : null;
	const lit = useMemo(() => {
		if (node) return neighboursOf(map, node.id);
		if (gap) return new Set([gap.subjectId, ...gap.nextActions].filter((id): id is string => Boolean(id)));
		return null;
	}, [map, node, gap]);
	const selectedNode = node?.id ?? null;
	const pick = (id: string | null) => select(id ? { kind: "node", id } : null);
	const bareRecord = selected?.kind === "record" ? selected.id : null;

	return (
		<div className="research-map">
			<div className="research-canvas" ref={box}>
				{layout.mode === "graph" ? <ResearchGraph layout={layout} selected={selectedNode} lit={lit} onSelect={pick} /> : <ResearchList layout={layout} selected={selectedNode} onSelect={pick} />}
			</div>
			<section className="research-gaps">
				<h5>缺口 {gaps.length}</h5>
				{gaps.length === 0 ? (
					<p className="hint">暂无缺口</p>
				) : (
					<ul>
						{gaps.map((g) => (
							<li key={g.id} className={gap?.id === g.id ? "selected" : ""}>
								<button onClick={() => select({ kind: "gap", id: g.id })}>
									<span className={`research-tag ${g.classification}`}>{CLASSIFICATION_LABEL[g.classification]}</span>
									{g.title}
									{g.subjectKind && <span className="hint"> · {kindLabel(g.subjectKind)}</span>}
								</button>
							</li>
						))}
					</ul>
				)}
			</section>
			<MapNotices notices={map.notices} />
			{map.coverage.notAssociated.length > 0 && (
				<section className="research-queue">
					<h5>待关联材料 {map.coverage.notAssociated.length}</h5>
					<ul>
						{map.coverage.notAssociated.map((b) => {
							const material = typeof (b.materialRef as { path?: unknown } | null)?.path === "string" ? String((b.materialRef as { path: string }).path) : JSON.stringify(b.materialRef);
							const href = renderRecord ? null : researchPageHref(scopeRef, b.target.id);
							return (
								<li key={b.id} className={bareRecord === b.target.id ? "selected" : ""}>
									{href ? (
										<a href={href} onClick={(e) => openInApp(e, href)}>
											<span className="research-tag unknown">{b.associationStatus}</span>
											<code>{material}</code>
										</a>
									) : (
										<button onClick={() => select(selectionFor(map, b.target.id))}>
											<span className="research-tag unknown">{b.associationStatus}</span>
											<code>{material}</code>
										</button>
									)}
									{b.rationale && <span className="hint"> · {b.rationale}</span>}
								</li>
							);
						})}
					</ul>
				</section>
			)}
			{node && (
				<section className="research-detail">
					<NodeDetail node={node} map={map} scopeRef={scopeRef} onSelect={select} onFocus={onFocus} />
					{renderRecord?.(node.id)}
				</section>
			)}
			{gap && (
				<section className="research-detail">
					<GapDetail gap={gap} map={map} scopeRef={scopeRef} onSelect={select} />
					{gap.target && renderRecord?.(gap.target.id)}
				</section>
			)}
			{bareRecord && renderRecord && <section className="research-detail">{renderRecord(bareRecord)}</section>}
		</div>
	);
}

function curve(e: PlacedResearchEdge): string {
	if (e.kind === "side") return `M ${e.x1} ${e.y1} L ${e.x2} ${e.y2}`;
	const bend = Math.max(18, Math.abs(e.y2 - e.y1) / 2) * (e.kind === "up" ? -1 : 1);
	return `M ${e.x1} ${e.y1} C ${e.x1} ${e.y1 + bend} ${e.x2} ${e.y2 - bend} ${e.x2} ${e.y2}`;
}

function ResearchGraph({ layout, selected, lit, onSelect }: { layout: Extract<ResearchLayout, { mode: "graph" }>; selected: string | null; lit: Set<string> | null; onSelect: (id: string | null) => void }) {
	return (
		<svg className="research-svg" width={layout.width} height={layout.height} viewBox={`0 0 ${layout.width} ${layout.height}`} role="group" aria-label="研究地图" onClick={() => onSelect(null)}>
			{layout.bands.map((band) => (
				<g key={band.kind} className="research-band">
					<rect x={0} y={band.y} width={layout.width} height={band.h} />
					<text className="research-band-name" x={12} y={band.y + 17}>
						{band.label}
					</text>
					<text className="research-band-count" x={layout.width - 12} y={band.y + 17} textAnchor="end">
						{band.count}
					</text>
					{band.count === 0 && (
						<text className="research-band-empty" x={12} y={band.y + 35}>
							暂无记录
						</text>
					)}
				</g>
			))}
			{layout.edges.map((e) => (
				<path key={e.id} className={`research-edge ${e.provenance}${lit ? (lit.has(e.from) && lit.has(e.to) ? " lit" : " faded") : ""}`} d={curve(e)}>
					<title>{e.type}</title>
				</path>
			))}
			{layout.nodes.map((n) => (
				<g
					key={n.id}
					data-id={n.id}
					className={`research-node ${n.state}${n.sourceStatus ? ` ${n.sourceStatus}` : ""}${selected === n.id ? " selected" : ""}${lit && !lit.has(n.id) ? " faded" : ""}`}
					role="button"
					tabIndex={0}
					aria-label={`${n.title}，${kindLabel(n.kind)}，${recordStatusLabel(n.state)}`}
					onClick={(ev) => (ev.stopPropagation(), onSelect(n.id))}
					onKeyDown={(ev) => (ev.key === "Enter" || ev.key === " ") && (ev.preventDefault(), onSelect(n.id))}
				>
					<title>{n.title}</title>
					<rect x={n.x} y={n.y} width={n.w} height={n.h} rx={7} />
					<text x={n.x + 12} y={n.y + n.h / 2 + 4.5}>
						{n.label}
					</text>
				</g>
			))}
		</svg>
	);
}

function ResearchList({ layout, selected, onSelect }: { layout: Extract<ResearchLayout, { mode: "list" }>; selected: string | null; onSelect: (id: string | null) => void }) {
	return (
		<div className="research-map-list">
			{layout.bands.map((band) => (
				<div key={band.kind}>
					<h6>
						{band.label} {band.nodes.length}
					</h6>
					{band.nodes.length === 0 ? (
						<p className="hint">暂无记录</p>
					) : (
						<ul>
							{band.nodes.map((n) => (
								<li key={n.id}>
									<button className={`research-chip${selected === n.id ? " selected" : ""}`} onClick={() => onSelect(n.id)}>
										{n.label}
									</button>
								</li>
							))}
						</ul>
					)}
				</div>
			))}
		</div>
	);
}

function RecordLink({ scopeRef, id }: { scopeRef: ResearchScopeRef; id: string }) {
	const href = researchPageHref(scopeRef, id);
	return (
		<a href={href} onClick={(e) => openInApp(e, href)}>
			打开记录
		</a>
	);
}

function NodeDetail({ node, map, scopeRef, onSelect, onFocus }: { node: ResearchMapNode; map: ResearchMapView; scopeRef: ResearchScopeRef; onSelect: (s: ResearchSelection) => void; onFocus?: (id: string) => void }) {
	const edges = map.edges.filter((e) => e.from === node.id || e.to === node.id);
	return (
		<>
			<h4>
				<span className={`research-tag ${node.state}`}>{recordStatusLabel(node.state)}</span>
				{kindLabel(node.kind)} · {node.label}
			</h4>
			<dl>
				<dt>来源</dt>
				<dd>{authorityLabel(node.authority)}</dd>
				<dt>版本</dt>
				<dd>
					r{node.revision}
					{node.headRevision !== node.revision ? ` / 最新 r${node.headRevision}` : ""} · {timeLabel(node.createdAt)}
				</dd>
				<dt>材料</dt>
				<dd>
					<Sources refs={node.refs} scopeRef={scopeRef} />
				</dd>
				{edges.length > 0 && (
					<>
						<dt>关联</dt>
						<dd>
							<ul className="research-sources">
								{edges.map((e) => {
									const other = e.from === node.id ? e.to : e.from;
									const target = map.nodes.find((x) => x.id === other);
									return (
										<li key={e.id}>
											<button className="research-chip" onClick={() => onSelect({ kind: "node", id: other })}>
												{target?.label ?? other}
											</button>{" "}
											<span className="hint">
												{e.type}
												{e.provenance === "unknown" ? " · 来源未知" : ""}
											</span>
										</li>
									);
								})}
							</ul>
						</dd>
					</>
				)}
			</dl>
			<div className="research-links">
				<RecordLink scopeRef={scopeRef} id={node.id} />
				{onFocus && (
					<button className="ghost" onClick={() => onFocus(node.id)}>
						展开邻域
					</button>
				)}
			</div>
		</>
	);
}

function GapDetail({ gap, map, scopeRef, onSelect }: { gap: ResearchMapGap; map: ResearchMapView; scopeRef: ResearchScopeRef; onSelect: (s: ResearchSelection) => void }) {
	const subject = gap.subjectId ? (map.nodes.find((n) => n.id === gap.subjectId) ?? null) : null;
	return (
		<>
			<h4>
				<span className={`research-tag ${gap.classification}`}>{CLASSIFICATION_LABEL[gap.classification]}</span>
				{gap.title}
			</h4>
			<dl>
				{gap.reason && (
					<>
						<dt>依据</dt>
						<dd>{gap.reason}</dd>
					</>
				)}
				<dt>规则</dt>
				<dd>
					{gap.basis.rule}
					{gap.basis.subjectRevision !== null ? ` · 对象版本 r${gap.basis.subjectRevision}` : ""}
					{gap.basis.checkedRefs.length ? ` · 已检查 ${gap.basis.checkedRefs.length} 条` : ""}
				</dd>
				{(subject || gap.subjectKind) && (
					<>
						<dt>对象</dt>
						<dd>
							{subject ? (
								<>
									<button className="research-chip" onClick={() => onSelect({ kind: "node", id: subject.id })}>
										{subject.label}
									</button>{" "}
									<span className="hint">{kindLabel(subject.kind)}</span>
								</>
							) : (
								kindLabel(gap.subjectKind!)
							)}
						</dd>
					</>
				)}
				{gap.revision !== null && (
					<>
						<dt>版本</dt>
						<dd>
							r{gap.revision}
							{gap.headRevision !== null && gap.headRevision !== gap.revision ? ` / 最新 r${gap.headRevision}` : ""}
						</dd>
					</>
				)}
				{gap.origin && (
					<>
						<dt>来源</dt>
						<dd>
							{gap.authority ? authorityLabel(gap.authority) : ""} · {gap.origin.actor}
							{gap.origin.sessionId ? ` · 对话 ${gap.origin.sessionId.slice(0, 8)}` : ""}
						</dd>
					</>
				)}
				<dt>材料</dt>
				<dd>
					<Sources refs={gap.refs} scopeRef={scopeRef} />
				</dd>
				{gap.nextActions.length > 0 && (
					<>
						<dt>下一步</dt>
						<dd>
							{gap.nextActions.map((id) => {
								const action = map.nodes.find((n) => n.id === id);
								return action ? (
									<button key={id} className="research-chip" onClick={() => onSelect({ kind: "node", id })}>
										{action.label}
									</button>
								) : (
									<code key={id}>{id}</code>
								);
							})}
						</dd>
					</>
				)}
			</dl>
			{gap.target && (
				<div className="research-links">
					<RecordLink scopeRef={scopeRef} id={gap.target.id} />
				</div>
			)}
		</>
	);
}
